import React, { createContext, useContext, useState, useCallback } from "react";
import { useApi } from "./ApiContext";

const ContentContext = createContext();

// Default designs for components
const defaultContent = {
	profileHeader: {
		statsLabels: {
			posts: "Books Read!",
			followers: "Followers",
			following: "Following",
		},
		tabs: [
			{
				label: "Books",
				id: "books",
				icon: "book-outline",
			},
			{
				label: "Awardo",
				id: "awards",
				icon: "ribbon-outline",
			},
		],
	},
	quickAccess: {
		// Posts Configuration
		enabled: true,
		// FeaturedAssets Configuration
		featuredAssets: {
			enabled: true,
			whoCanCreate: "mods",
			label: "Featured Assets",
			icon: "star-outline",
		},
		// CreateAssets Configuration
		createAssets: {
			enabled: true,
			whoCanCreate: "mods",
			whoCanView: "all",
			label: "Create Assets",
			icon: "create-outline",
		},
		// Individual items data
		posts: {
			label: "Posts",
			icon: "pencil-outline",
			whoCanCreate: "users",
			whoCanView: "all",
		},
	},
	productsPage: {
		// FeaturedAssets Configuration
		productItem: {
			label: "Product",
			labelSingular: "product",
			labelPlural: "products",
			icon: "pricetags-outline",
		},
		pageTitle: "Product",
		pageIcon: "tag",
		products: {
			whoCanCreate: "admins",
		},
		productDetail: {
			icon: "box-open",
			howToBuy: "Add to Cart!",
		},
	},
	feedPage: {
		pageTitle: "Feed",
		pageIcon: "newspaper",
	},
	eventsPage: {
		pageTitle: "Events",
		pageIcon: "calendar",
	},
	communityPage: {
		pageTitle: "Community",
		pageIcon: "users",
	},
	profilePage: {
		pageTitle: "Profile",
		pageIcon: "person",
	},
};

// Deep merge utility: merges source into target, filling missing nested properties from defaults
function deepMerge(target, source) {
	if (typeof target !== "object" || target === null) return source;
	if (typeof source !== "object" || source === null) return target;

	const result = Array.isArray(target) ? [...target] : { ...target };
	for (const key of Object.keys(source)) {
		if (typeof source[key] === "object" && source[key] !== null && !Array.isArray(source[key]) && typeof target[key] === "object" && target[key] !== null && !Array.isArray(target[key])) {
			result[key] = deepMerge(target[key], source[key]);
		} else {
			result[key] = source[key];
		}
	}
	return result;
}

export const ContentProvider = ({ children }) => {
	const [content, setContent] = useState(defaultContent);
	const { api } = useApi();

	const fetchContent = useCallback(
		(componentType) => {
			// First check if component exists in current state
			if (!content[componentType]) {
				// If not in current state, return default content
				return defaultContent[componentType] || null;
			}

			// Component exists in current state - for top level values that might be
			// missing, selectively add them from defaults without overriding anything
			const currentComponentContent = content[componentType];
			const defaultComponentContent = defaultContent[componentType] || {};

			// Only hydrate missing top-level properties - not arrays or deeply nested objects
			// This preserves user customizations like array length changes
			const hydratedContent = { ...currentComponentContent };

			// Add any missing top-level properties from defaults
			Object.keys(defaultComponentContent).forEach((key) => {
				// If property is missing entirely, add it from defaults
				if (hydratedContent[key] === undefined) {
					hydratedContent[key] = defaultComponentContent[key];
				}
			});
			return hydratedContent;
		},
		[content]
	);
	const contentSnapshot = useCallback(() => {
		return content;
	}, [content]);
	const contentRestore = useCallback((newContent) => {
		setContent((prev) => deepMerge(defaultContent, { ...prev, ...newContent }));
	}, []);
	const updateContent = useCallback((componentType, newContent) => {
		setContent((prev) => {
			const currentComponent = prev[componentType] || {};
			const defaultComponentContent = defaultContent[componentType] || {};
			// Deep merge: newContent over currentComponent over defaultComponentContent
			const merged = deepMerge(defaultComponentContent, deepMerge(currentComponent, newContent));
			return {
				...prev,
				[componentType]: merged,
			};
		});
	}, []);

	return (
		<ContentContext.Provider
			value={{
				content,
				fetchContent,
				updateContent,
				contentSnapshot,
				contentRestore,
			}}>
			{children}
		</ContentContext.Provider>
	);
};

export const useContent = () => {
	const context = useContext(ContentContext);
	if (!context) {
		throw new Error("useContent must be used within a ContentProvider");
	}
	return context;
};
